import { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { Tabs } from '@/modules/corporate/components/LearnerAndGroupDialog/types';
import { getTabs, OVERRIDES } from '@/modules/corporate/components/LearnerAndGroupDialog/utils';
import { WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import useSeeAllGroups from '../useSeeAllGroups';
import useSeeAllLearners from '../useSeeAllLearners';

type UseSeeAllLearnerAndGroupsProps = {
  learnerIds: string[];
  userGroupIds: string[];
  importedUserGroupIds?: string[];
  removedUserIds?: string[];
};

export const useIsGroupVisible = () => {
  const { canAccess: isGroupListAccess } = useWorkspaceAccessControl(
    WorkspaceActions.List,
    WorkspaceSubjects.UserCollection,
  );
  return isGroupListAccess;
};

export default function useSeeAllLearnerAndGroups(props: UseSeeAllLearnerAndGroupsProps) {
  const { t } = useTranslation();
  const { learnerIds, userGroupIds, importedUserGroupIds, removedUserIds } = props;
  const [inViewTab, setInViewTab] = useState(Tabs.Learners);

  const isGroupVisible = useIsGroupVisible();
  const tabs = useMemo(() => {
    return {
      value: inViewTab,
      tabs: getTabs({ t, isGroupVisible, isUserVisible: true }),
      onChange: (val: string) => setInViewTab(val as Tabs),
      overrides: OVERRIDES,
    };
  }, [inViewTab, isGroupVisible, t]);

  const seeAllLearnerData = useSeeAllLearners({
    learnerIds,
    removedUserIds,
    importedUserGroupIds,
  });
  const seeAllGroupData = useSeeAllGroups({
    userGroupIds,
  });

  const dialogData = {
    [Tabs.Learners]: seeAllLearnerData,
    [Tabs.Groups]: seeAllGroupData,
  };

  return {
    data: dialogData[inViewTab],
    tabs,
    inViewTab,
  };
}
